import type { ChronicleDeviceState } from '@/atoms/chronicle';
import {
  clearRemoteConfigHardwareSignals,
  setRemoteConfigCustomSignals,
} from '@/lib/firebase/custom-signals';
import { bootOrUpdate } from '@/lib/Intercom';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import * as amplitude from '@amplitude/analytics-browser';
import type * as IntercomTypes from '@intercom/messenger-js-sdk/dist/types';

/**
 * Profile-property keys. Deliberately identical to the `hardware_*` event
 * property keys in `chronicle-device-props.ts` so Amplitude profiles join
 * cleanly against hardware events, and to the iOS keys for cross-platform
 * parity.
 */
export const HARDWARE_SERIAL_NUMBER_PROPERTY = 'hardware_serial_number';
export const HARDWARE_FW_VERSION_PROPERTY = 'hardware_fw_version';

/** The device-state fields the profile write reads — nothing else re-triggers it. */
export type HardwareProfileSource = Pick<
  ChronicleDeviceState,
  'deviceName' | 'serialNumber' | 'firmwareVersion'
>;

interface HardwareProfileMetadata {
  serialNumber: string;
  firmwareVersion: string | null;
}

/**
 * Last value pair written in this page load. Support agents and analysts only
 * need the current device, so re-sending an unchanged pair on every reconnect
 * would cost identify calls for no new information.
 *
 * Cleared on logout (`authModel`) so a different account signing in on the same
 * install gets the metadata re-sent to its fresh analytics/Intercom identity.
 */
let lastSyncedMetadata: HardwareProfileMetadata | null = null;

export function resetHardwareProfileMetadataCache(): void {
  lastSyncedMetadata = null;
}

/**
 * `serialNumber` is null on legacy device records where serial extraction
 * fails; the BLE advertised name is the serial on this firmware, so it is the
 * same fallback `buildChronicleDeviceProps` applies to the event properties.
 */
export function selectHardwareProfileMetadata(
  state: HardwareProfileSource
): HardwareProfileMetadata | null {
  const serialNumber = state.serialNumber ?? state.deviceName;
  if (!serialNumber) return null;
  return { serialNumber, firmwareVersion: state.firmwareVersion };
}

/**
 * Forwards the linked Heidi Remote's serial number and firmware version to the
 * Amplitude user profile, Intercom contact, and Remote Config custom signals
 * (shared Mobile+Desktop keys `hardware_serial_number` /
 * `hardware_fw_version`), so support/analytics and RC device targeting can see
 * which device a clinician has without digging into individual events.
 *
 * Fed by `chronicleDeviceStateAtom`, which carries the live BLE values while
 * connected and the backend `hardware_device` record after a cold start — so
 * the Amplitude/Intercom profile stays populated with the remote powered off.
 * RC hardware signals clear when the device is unpaired or unknown so targeting
 * does not keep a stale serial.
 */
export function syncHardwareProfileMetadata(state: HardwareProfileSource): void {
  // TELEMETRY-GATE: F1 function-entry gate. Mirrors the canonical pattern in
  // src/providers/amplitude-user-properties.ts — no Amplitude `.identify()` and
  // no Intercom egress on a suppressed build. See
  // .ai-context/onprem-guardrails.md (rule F1).
  if (!isSaasTelemetryEnabled) return;

  const metadata = selectHardwareProfileMetadata(state);
  if (!metadata) {
    // Unpaired / unknown — clear RC hardware signals; Amplitude/Intercom keep
    // last-known values below via the early return on a null selection.
    clearRemoteConfigHardwareSignals();
    return;
  }

  // Always push RC signals; the signals module no-ops when values are unchanged.
  setRemoteConfigCustomSignals({
    hardware_serial_number: metadata.serialNumber,
    hardware_fw_version: metadata.firmwareVersion,
  });

  if (
    lastSyncedMetadata?.serialNumber === metadata.serialNumber &&
    lastSyncedMetadata.firmwareVersion === metadata.firmwareVersion
  ) {
    return;
  }
  lastSyncedMetadata = metadata;

  const attributes: Record<string, string> = {
    [HARDWARE_SERIAL_NUMBER_PROPERTY]: metadata.serialNumber,
  };
  // Omitted rather than sent as `NA` (the event-property convention): a profile
  // is long-lived, so a placeholder would outlive the gap it describes.
  if (metadata.firmwareVersion) {
    attributes[HARDWARE_FW_VERSION_PROPERTY] = metadata.firmwareVersion;
  }

  try {
    const identify = new amplitude.Identify();
    for (const [property, value] of Object.entries(attributes)) {
      identify.set(property, value);
    }
    amplitude.identify(identify);
  } catch {
    // Amplitude unavailable (SSR, blocked by adblock, etc.) — non-fatal.
  }

  // `update` merges custom attributes into the current contact; the on-prem and
  // locale gates live inside `bootOrUpdate`.
  bootOrUpdate(attributes as IntercomTypes.IntercomSettings);
}
